import React, { useEffect, useRef } from "react";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";

const CARD_CONFIGS = [
  {
    key: "page",
    label: "Pages",
    sublabel: "Completed",
    totalKey: "total_page",
    completedKey: "total_completed_pages",
    icon: MenuBookOutlinedIcon,
    gradient: ["#FCA43B", "#FDBE6D"],
    glowColor: "rgba(252, 164, 59, 0.28)",
    trackColor: "rgba(252, 164, 59, 0.12)",
  },

  {
    key: "test",
    label: "Tests",
    sublabel: "Completed",
    totalKey: "total_test",
    completedKey: "total_test_completed",
    icon: AssignmentOutlinedIcon,
    gradient: ["#D4F92D", "#E0FA5F"],
    glowColor: "rgba(212, 249, 45, 0.28)",
    trackColor: "rgba(212, 249, 45, 0.12)",
  },
  {
    key: "quiz",
    label: "Quizzes",
    sublabel: "Completed",
    totalKey: "total_quiz",
    completedKey: "total_quiz_completed",
    icon: QuizOutlinedIcon,
    gradient: ["#85EEAD", "#A8F5C2"],
    glowColor: "rgba(133, 238, 173, 0.28)",
    trackColor: "rgba(133, 238, 173, 0.12)",
  },
];

const RADIUS = 34;
const STROKE = 5;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function AnimatedRing({ percentage, gradient, gradientId, trackColor }) {
  const offset = CIRCUMFERENCE - (percentage / 100) * CIRCUMFERENCE;
  return (
    <svg
      width={100}
      height={100}
      viewBox="0 0 90 90"
      style={{ display: "block", flexShrink: 0 }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={gradient[0]} />
          <stop offset="100%" stopColor={gradient[1]} />
        </linearGradient>
      </defs>
      <circle
        cx={45}
        cy={45}
        r={RADIUS}
        fill="none"
        stroke={trackColor}
        strokeWidth={STROKE}
      />
      <circle
        cx={45}
        cy={45}
        r={RADIUS}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={offset}
        transform="rotate(-90 45 45)"
        style={{
          transition: "stroke-dashoffset 1.1s cubic-bezier(0.4,0,0.2,1)",
        }}
      />
    </svg>
  );
}

function MetricCard({ config, total, completed, index }) {
  const pct =
    total > 0 ? Math.min(Math.round((completed / total) * 100), 100) : 0;
  const Icon = config.icon;
  const gradientId = `ring-grad-${config.key}`;

  return (
    <div
      className="metric-card-v2"
      style={{ animationDelay: `${index * 0.1 + 0.05}s` }}
    >
      <div className="metric-card-wrapper">
        <span
          className="metric-card-v2__glow"
          style={{ background: config.glowColor }}
        />
        <div className="metric-card-v2__header">
          <div
            className="metric-card-v2__icon-wrap"
            style={{
              background: `linear-gradient(135deg, ${config.gradient[0]}, ${config.gradient[1]})`,
              boxShadow: `0 6px 18px ${config.glowColor}`,
            }}
          >
            <Icon style={{ color: "#fff", fontSize: 22 }} />
          </div>
          <div className="metric-card-v2__label-wrap">
            <p className="metric-card-v2__label">{config.label}</p>
            <p className="metric-card-v2__sublabel">{config.sublabel}</p>
          </div>
        </div>{" "}
        <div className="metric-card-v2__body">
          <div className="metric-card-v2__stats">
            <div className="metric-card-v2__stat">
              <span
                className="metric-card-v2__stat-val"
                style={{ color: config.gradient[0] }}
              >
                {completed}
              </span>
              <span className="metric-card-v2__stat-key">Done</span>
            </div>
            <div className="metric-card-v2__divider" />
            <div className="metric-card-v2__stat">
              <span className="metric-card-v2__stat-val metric-card-v2__stat-val--muted">
                {total}
              </span>
              <span className="metric-card-v2__stat-key">Total</span>
            </div>
          </div>
        </div>{" "}
      </div>
      <div className="metric-card-v2__ring-wrap">
        <AnimatedRing
          percentage={pct}
          gradient={config.gradient}
          gradientId={gradientId}
          trackColor={config.trackColor}
        />
        <div className="metric-card-v2__ring-label">
          <span
            className="metric-card-v2__pct"
            style={{ color: config.gradient[0] }}
          >
            {pct}%
          </span>
        </div>
      </div>
    </div>
  );
}

const MetricGrid = ({ metrics }) => {
  const cards = CARD_CONFIGS.map((cfg, i) => {
    const match = metrics?.find(
      (m) => m.type === cfg.key || m.totalKey === cfg.totalKey,
    );
    const total = match?.total ?? match?.total_count ?? 0;
    const completed = match?.completed ?? 0;
    return { config: cfg, total, completed, index: i };
  });

  return (
    <div className="metrics-grid-v2">
      {cards.map(({ config, total, completed, index }) => (
        <MetricCard
          key={config.key}
          config={config}
          total={total}
          completed={completed}
          index={index}
        />
      ))}
    </div>
  );
};

export default MetricGrid;
